import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiVolume2, FiCheck, FiUser, FiPlay, FiPause, FiLoader, FiRefreshCw } = FiIcons;

const VoiceSelector = ({ 
  languageCode, 
  selectedVoice, 
  onVoiceChange, 
  onAutoVoiceSelect,  // NUEVO: Callback para auto-selección
  shouldAutoSelect,   // NUEVO: Flag para determinar si debe auto-seleccionar
  isOpen, 
  onToggle,
  onClose,
  label 
}) => {
  const { t } = useLanguage();
  const [voices, setVoices] = useState([]);
  const [filteredVoices, setFilteredVoices] = useState([]);
  const [isTestingVoice, setIsTestingVoice] = useState(null);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const currentUtterance = useRef(null);
  const voicesCheckInterval = useRef(null);
  const loadingTimeout = useRef(null);

  // Mapeo COMPLETO y FUNCIONAL de códigos de idioma
  const getVoiceLanguage = (languageCode) => {
    const voiceMap = {
      'af': ['af-ZA', 'af'],
      'sq': ['sq-AL', 'sq'],
      'am': ['am-ET', 'am'],
      'ar': ['ar-SA', 'ar-EG', 'ar-AE', 'ar-MA', 'ar'],
      'hy': ['hy-AM', 'hy'],
      'az': ['az-AZ', 'az'],
      'eu': ['eu-ES', 'eu'],
      'be': ['be-BY', 'be'],
      'bn': ['bn-BD', 'bn-IN', 'bn'],
      'bs': ['bs-BA', 'bs'],
      'bg': ['bg-BG', 'bg'],
      'ca': ['ca-ES', 'ca'],
      'zh': ['zh-CN', 'zh-Hans', 'zh-cmn', 'cmn-Hans-CN', 'zh'],
      'zh-TW': ['zh-TW', 'zh-Hant', 'zh-cmn-Hant', 'cmn-Hant-TW', 'zh-HK'],
      'hr': ['hr-HR', 'hr'],
      'cs': ['cs-CZ', 'cs'],
      'da': ['da-DK', 'da'],
      'nl': ['nl-NL', 'nl-BE', 'nl'],
      'en': ['en-US', 'en-GB', 'en-AU', 'en-CA', 'en-IN', 'en-IE', 'en-ZA', 'en'],
      'et': ['et-EE', 'et'],
      'tl': ['tl-PH', 'fil-PH', 'tl', 'fil'],
      'fi': ['fi-FI', 'fi'],
      'fr': ['fr-FR', 'fr-CA', 'fr-BE', 'fr-CH', 'fr'],
      'ka': ['ka-GE', 'ka'],
      'de': ['de-DE', 'de-AT', 'de-CH', 'de'],
      'el': ['el-GR', 'el'],
      'gu': ['gu-IN', 'gu'],
      'he': ['he-IL', 'iw-IL', 'he', 'iw'],
      'hi': ['hi-IN', 'hi'],
      'hu': ['hu-HU', 'hu'],
      'is': ['is-IS', 'is'],
      'id': ['id-ID', 'id'],
      'ga': ['ga-IE', 'ga'],
      'it': ['it-IT', 'it-CH', 'it'],
      'ja': ['ja-JP', 'ja'],
      'kn': ['kn-IN', 'kn'],
      'kk': ['kk-KZ', 'kk'],
      'ko': ['ko-KR', 'ko'],
      'lv': ['lv-LV', 'lv'],
      'lt': ['lt-LT', 'lt'],
      'mk': ['mk-MK', 'mk'],
      'ms': ['ms-MY', 'ms'],
      'ml': ['ml-IN', 'ml'],
      'mt': ['mt-MT', 'mt'],
      'mr': ['mr-IN', 'mr'],
      'ne': ['ne-NP', 'ne'],
      'no': ['nb-NO', 'nn-NO', 'no-NO', 'no', 'nb', 'nn'],
      'fa': ['fa-IR', 'fa'],
      'pl': ['pl-PL', 'pl'],
      'pt': ['pt-PT', 'pt-BR', 'pt'],
      'pa': ['pa-IN', 'pa'],
      'ro': ['ro-RO', 'ro'],
      'ru': ['ru-RU', 'ru'],
      'sr': ['sr-RS', 'sr'],
      'si': ['si-LK', 'si'],
      'sk': ['sk-SK', 'sk'],
      'sl': ['sl-SI', 'sl'],
      'es': ['es-ES', 'es-MX', 'es-AR', 'es-CO', 'es-CL', 'es-PE', 'es-VE', 'es-US', 'es'],
      'sw': ['sw-KE', 'sw'],
      'sv': ['sv-SE', 'sv'],
      'ta': ['ta-IN', 'ta-LK', 'ta'],
      'te': ['te-IN', 'te'],
      'th': ['th-TH', 'th'],
      'tr': ['tr-TR', 'tr'],
      'uk': ['uk-UA', 'uk'],
      'ur': ['ur-PK', 'ur-IN', 'ur'],
      'vi': ['vi-VN', 'vi'],
      'cy': ['cy-GB', 'cy']
    };
    return voiceMap[languageCode] || ['en-US', 'en'];
  };

  // Función para evaluar la calidad de la voz
  const getVoiceQuality = (voice) => {
    const name = voice.name.toLowerCase();
    let quality = 'standard';
    let score = 50;

    // Detectar voces premium
    if (name.includes('neural') || name.includes('premium') || name.includes('enhanced') || name.includes('wavenet')) {
      quality = 'premium';
      score = 100;
    } else if (name.includes('plus') || name.includes('improved') || voice.localService || name.includes('natural')) {
      quality = 'enhanced';
      score = 80;
    } else if (name.includes('quality') || name.includes('default') || name.includes('standard')) {
      quality = 'good';
      score = 60;
    }

    // Bonus por sistemas conocidos
    if (name.includes('microsoft') || name.includes('google') || name.includes('apple') || name.includes('amazon')) {
      score += 20;
    }
    
    // Penalizar voces problemáticas
    if (name.includes('espeak') || name.includes('robot') || name.includes('synthetic') || name.includes('novelai')) {
      score -= 50;
    }

    if (voice.localService) score += 15;

    return { quality, score };
  };

  // Función MEJORADA para determinar el género de la voz
  const determineGender = (voice) => {
    const name = voice.name.toLowerCase();
    
    // Patrones femeninos más específicos
    const femalePatterns = [
      'female', 'woman', 'samantha', 'susan', 'victoria', 'zira', 'helen', 'sara', 'jenny', 
      'alice', 'claire', 'lisa', 'kate', 'nina', 'olivia', 'paula', 'vera', 'joanna', 
      'salli', 'kimberly', 'amy', 'hazel', 'ivy', 'ruth', 'diane', 'nora', 'grace', 
      'julia', 'carmen', 'laura', 'monica', 'andrea', 'maria', 'elena', 'sophia', 
      'anna', 'emma', 'eva', 'isabella', 'natasha', 'karen', 'linda', 'patricia', 
      'barbara', 'elizabeth', 'jennifer', 'mary', 'margaret', 'dorothy', 'betty', 
      'sandra', 'donna', 'carol', 'sharon', 'michelle', 'emily', 'deborah', 'ashley', 
      'sarah', 'alexis', 'jessica', 'taylor', 'hannah', 'madison', 'amanda', 'stephanie', 
      'brittany', 'rebecca', 'danielle', 'rachel', 'megan', 'lauren', 'kayla', 'christina', 
      'courtney', 'vanessa', 'heather', 'nicole', 'amber', 'tiffany', 'shannon', 'jamie', 
      'crystal', 'paige', 'brooke', 'allison', 'erin', 'lindsey', 'whitney', 'brittney', 
      'chelsea', 'jasmine', 'cassandra', 'angela', 'briana', 'katelyn', 'shelby', 'morgan', 
      'destiny', 'sydney', 'alexandra', 'marissa', 'miranda', 'gabrielle', 'sierra', 
      'diamond', 'jenna', 'catherine', 'ariel', 'sabrina', 'chloe', 'makayla', 'gabriella', 
      'breanna', 'mackenzie', 'jacqueline', 'molly', 'bianca', 'sofia', 'camila', 
      'fernanda', 'valeria', 'alejandra', 'mariana', 'gabriela', 'daniela', 'adriana', 
      'paola', 'carolina', 'lucia', 'natalia', 'jocelyn', 'melissa'
    ];
    
    // Patrones masculinos más específicos
    const malePatterns = [
      'male', 'man', 'david', 'mark', 'daniel', 'alex', 'jorge', 'thomas', 'matthew', 
      'brian', 'justin', 'kevin', 'joey', 'russell', 'miguel', 'carlos', 'antonio', 
      'jose', 'john', 'james', 'robert', 'michael', 'william', 'richard', 'christopher', 
      'diego', 'pedro', 'paul', 'steven', 'kenneth', 'joshua', 'andrew', 'anthony', 
      'donald', 'edward', 'ronald', 'timothy', 'jason', 'jeffrey', 'ryan', 'jacob', 
      'gary', 'nicholas', 'eric', 'jonathan', 'stephen', 'larry', 'scott', 'brandon', 
      'benjamin', 'samuel', 'gregory', 'frank', 'raymond', 'alexander', 'patrick', 
      'jack', 'dennis', 'jerry', 'tyler', 'aaron', 'henry', 'adam', 'douglas', 
      'nathan', 'peter', 'zachary', 'kyle', 'walter', 'harold', 'jeremy', 'ethan', 
      'carl', 'arthur', 'wayne', 'lawrence', 'jordan', 'billy', 'bruce', 'ralph', 
      'eugene', 'louis', 'philip', 'bobby', 'johnny', 'mason', 'roy', 'victor', 
      'tommy', 'leonard', 'gabriel', 'willie', 'craig', 'phillip', 'charles', 'juan', 
      'francisco', 'manuel', 'alejandro', 'rafael', 'roberto', 'fernando', 'luis', 
      'alberto', 'ramon', 'jesus', 'angel', 'ricardo', 'salvador', 'ruben', 'armando', 
      'sergio', 'pablo', 'oscar', 'gerardo', 'eduardo', 'alfredo', 'arturo', 'mauricio', 
      'raul', 'enrique', 'mario', 'guadalupe', 'felipe', 'hector', 'ignacio', 'javier', 
      'lorenzo', 'cesar', 'adrian', 'martin', 'rodrigo', 'esteban', 'leonardo', 'andres', 
      'emilio', 'hugo', 'ivan', 'abraham', 'ismael', 'santiago', 'jaime', 'joaquin', 
      'marco', 'aurelio', 'nicolas', 'cristian', 'fabian', 'sebastian', 'mateo', 
      'nathaniel', 'elijah', 'noah', 'logan', 'liam', 'oliver', 'lucas', 'carter', 
      'owen', 'wyatt', 'luke', 'jayden', 'dylan', 'grayson', 'levi', 'isaac', 'julian', 
      'jaxon', 'lincoln', 'caleb', 'asher', 'leo', 'isaiah', 'josiah', 'colton', 
      'parker', 'ian', 'connor', 'hunter', 'cameron', 'colin', 'hudson'
    ];
    
    // Buscar patrones femeninos
    for (const pattern of femalePatterns) {
      if (name.includes(pattern)) {
        return 'female';
      }
    }
    
    // Buscar patrones masculinos
    for (const pattern of malePatterns) {
      if (name.includes(pattern)) {
        return 'male';
      }
    }
    
    return 'neutral';
  };

  // Cargar voces con verificación de funcionalidad MEJORADA
  const loadVoices = async () => {
    if (!('speechSynthesis' in window)) {
      console.warn('Speech synthesis not supported');
      setVoicesLoaded(true);
      setLoadingVoices(false);
      return;
    }

    setLoadingVoices(true);
    
    try {
      const availableVoices = speechSynthesis.getVoices();
      
      if (availableVoices.length === 0) {
        console.warn('No voices available yet');
        setLoadingVoices(false);
        return;
      }

      console.log(`🎤 Loading voices for language: ${languageCode}`);
      console.log(`📢 Total available voices: ${availableVoices.length}`);

      const targetLanguages = getVoiceLanguage(languageCode);
      console.log(`🎯 Target language patterns:`, targetLanguages);

      // Filtrar voces por idioma
      const languageVoices = availableVoices.filter(voice => {
        // CRITICAL FIX: Verificar que la voz es válida
        if (!voice || !voice.lang || !voice.name || !voice.voiceURI) {
          return false;
        }

        const voiceLang = voice.lang.toLowerCase();
        
        const isLanguageMatch = targetLanguages.some(targetLang => {
          const targetLower = targetLang.toLowerCase();
          return (
            voiceLang === targetLower ||
            voiceLang.startsWith(targetLower) ||
            targetLower.startsWith(voiceLang.split('-')[0])
          );
        });

        // Filtrar voces problemáticas conocidas
        const isValidVoice = !/espeak|festival|robot|synthetic|novelai/i.test(voice.name);

        const matches = isLanguageMatch && isValidVoice;
        
        if (matches) {
          console.log(`✅ Voice matched: ${voice.name} (${voice.lang})`);
        }

        return matches;
      });

      console.log(`🔍 Filtered voices for ${languageCode}:`, languageVoices.length);

      // Sistema de fallback inteligente
      let finalVoices = languageVoices;
      if (finalVoices.length === 0) {
        console.log(`🔄 No direct matches, searching for compatible voices...`);
        
        const langBase = languageCode.split('-')[0];
        const compatibleVoices = availableVoices.filter(voice => {
          if (!voice || !voice.lang) return false;
          const voiceLangBase = voice.lang.split('-')[0].toLowerCase();
          return voiceLangBase === langBase.toLowerCase();
        });

        finalVoices = compatibleVoices;
        console.log(`🔗 Compatible voices found:`, finalVoices.length);
      }

      // Fallback a inglés si no hay voces
      if (finalVoices.length === 0 && languageCode !== 'en') {
        console.log(`🇺🇸 No compatible voices, using English fallback...`);
        const englishVoices = availableVoices.filter(voice => 
          voice && voice.lang && voice.lang.toLowerCase().startsWith('en')
        );
        finalVoices = englishVoices;
        console.log(`🗣️ English fallback voices:`, finalVoices.length);
      }

      // Limitar número de voces para mejor rendimiento
      const limitedVoices = finalVoices.slice(0, 20);

      if (limitedVoices.length === 0) {
        console.warn(`⚠️ No voices found for ${languageCode}`);
        setFilteredVoices([]);
        setVoicesLoaded(true);
        setLoadingVoices(false);
        return;
      }

      // CRITICAL FIX: Test de funcionalidad simplificado y más rápido
      console.log(`🧪 Testing functionality of ${limitedVoices.length} voices...`);
      
      const functionalVoices = [];
      
      // Test rápido y simple
      for (let i = 0; i < limitedVoices.length; i++) {
        const voice = limitedVoices[i];
        
        // Verificación básica de validez
        if (voice && voice.voiceURI && voice.name && voice.lang) {
          try {
            // Test simple: intentar crear utterance con la voz
            const testUtterance = new SpeechSynthesisUtterance('test');
            testUtterance.voice = voice;
            
            // Si no hay error, la voz es válida
            functionalVoices.push(voice);
          } catch (error) {
            console.warn(`❌ Voice ${voice.name} failed basic test:`, error);
          }
        }
      }

      console.log(`✅ Functional voices: ${functionalVoices.length} out of ${limitedVoices.length}`);

      // Si no hay voces funcionales después de las pruebas, usar todas
      let processedVoices;
      if (functionalVoices.length === 0) {
        console.log(`🔄 No functional voices detected in tests, using all available voices`);
        processedVoices = limitedVoices;
      } else {
        processedVoices = functionalVoices;
      }

      // Procesar y enriquecer información de las voces
      const enrichedVoices = processedVoices.map(voice => {
        const { quality, score } = getVoiceQuality(voice);
        const gender = determineGender(voice);
        
        let displayName = voice.name
          .replace(/^(Microsoft|Google|Apple|Amazon|Samsung|eSpeak|Nuance)\s+/i, '')
          .replace(/\s+\([^)]*\)/g, '')
          .trim();

        if (displayName.length < 3) {
          displayName = voice.name;
        }

        return {
          // CRITICAL FIX: Mantener la referencia original del objeto voice
          originalVoice: voice,
          voiceURI: voice.voiceURI,
          name: voice.name,
          lang: voice.lang,
          localService: voice.localService,
          default: voice.default,
          quality,
          score,
          gender,
          displayName,
          isLocal: voice.localService,
          fullName: voice.name,
          isFunctional: true
        };
      });

      // Ordenar por calidad y disponibilidad
      enrichedVoices.sort((a, b) => {
        // Priorizar voces locales
        if (a.isLocal !== b.isLocal) {
          return b.isLocal ? 1 : -1;
        }
        
        // Luego por calidad
        if (a.quality !== b.quality) {
          const qualityOrder = { premium: 4, enhanced: 3, good: 2, standard: 1 };
          return qualityOrder[b.quality] - qualityOrder[a.quality];
        }
        
        // Luego por género (preferir variedad)
        if (a.gender !== b.gender) {
          const genderOrder = { female: 3, male: 2, neutral: 1 };
          return genderOrder[b.gender] - genderOrder[a.gender];
        }
        
        return b.score - a.score;
      });

      setVoices(availableVoices);
      setFilteredVoices(enrichedVoices);
      setVoicesLoaded(true);
      setLoadingVoices(false);

      // NUEVO: Auto-seleccionar la mejor voz disponible SOLO si se requiere
      if (shouldAutoSelect && enrichedVoices.length > 0) {
        const bestVoice = enrichedVoices[0];
        console.log(`🎯 Auto-selecting best voice: ${bestVoice.name} for ${languageCode}`);
        
        // Usar el callback de auto-selección en lugar del callback normal
        if (onAutoVoiceSelect) {
          onAutoVoiceSelect(bestVoice);
        } else {
          // Fallback al callback normal si no hay callback de auto-selección
          onVoiceChange(bestVoice);
        }
      }

      console.log(`🎉 Successfully processed ${enrichedVoices.length} voices for ${languageCode}`);
      
    } catch (error) {
      console.error('❌ Error loading voices:', error);
      setLoadingVoices(false);
      setVoicesLoaded(true);
    }
  };

  // Recargar voces manualmente
  const reloadVoices = () => {
    setVoicesLoaded(false);
    setFilteredVoices([]);
    setLoadingVoices(false);
    
    // Limpiar timeouts
    if (loadingTimeout.current) {
      clearTimeout(loadingTimeout.current);
    }
    if (voicesCheckInterval.current) {
      clearInterval(voicesCheckInterval.current);
    }
    
    // Recargar después de un breve delay
    setTimeout(loadVoices, 100);
  };

  useEffect(() => {
    // Reset cuando cambie el idioma
    setVoicesLoaded(false);
    setFilteredVoices([]);
    setLoadingVoices(false);
    
    // Limpiar timeouts anteriores
    if (loadingTimeout.current) {
      clearTimeout(loadingTimeout.current);
    }
    if (voicesCheckInterval.current) {
      clearInterval(voicesCheckInterval.current);
    }
    
    // Cargar voces inmediatamente si están disponibles
    const currentVoices = speechSynthesis.getVoices();
    if (currentVoices.length > 0) {
      loadVoices();
    } else {
      // Si no hay voces, configurar verificación periódica
      let attempts = 0;
      const maxAttempts = 100; // 5 segundos máximo
      
      voicesCheckInterval.current = setInterval(() => {
        attempts++;
        const voices = speechSynthesis.getVoices();
        
        if (voices.length > 0) {
          clearInterval(voicesCheckInterval.current);
          loadVoices();
        } else if (attempts >= maxAttempts) {
          clearInterval(voicesCheckInterval.current);
          console.warn('⏰ Voices not loaded after timeout');
          setVoicesLoaded(true);
          setLoadingVoices(false);
        }
      }, 50);
    }

    // Listener para cambios en las voces
    const handleVoicesChanged = () => {
      console.log('🔄 Voices changed event fired');
      setTimeout(loadVoices, 100);
    };

    if ('speechSynthesis' in window) {
      speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
    }

    // Timeout de seguridad
    loadingTimeout.current = setTimeout(() => {
      if (!voicesLoaded) {
        console.warn('⏰ Voice loading timeout reached');
        setVoicesLoaded(true);
        setLoadingVoices(false);
      }
    }, 8000);

    return () => {
      if ('speechSynthesis' in window) {
        speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
      }
      if (voicesCheckInterval.current) {
        clearInterval(voicesCheckInterval.current);
      }
      if (loadingTimeout.current) {
        clearTimeout(loadingTimeout.current);
      }
    };
  }, [languageCode, shouldAutoSelect]); // NUEVO: Incluir shouldAutoSelect en dependencias

  // Función CORREGIDA para probar voz con texto de muestra - SIN ERRORES DE INTERRUPCIÓN
  const testVoice = (voice, event) => {
    event?.stopPropagation();
    
    if (!('speechSynthesis' in window) || !voice || !voice.originalVoice) {
      console.warn('🚫 Speech synthesis not available or invalid voice');
      return;
    }

    // Si ya está probando esta voz, detener SUAVEMENTE
    if (isTestingVoice === voice.voiceURI) {
      stopCurrentTestSafely();
      return;
    }

    // Detener cualquier síntesis anterior SUAVEMENTE
    stopCurrentTestSafely();

    const sampleTexts = {
      'es': 'Hola, esta es una prueba de voz en español.',
      'en': 'Hello, this is a voice test in English.',
      'fr': 'Bonjour, ceci est un test de voix en français.',
      'de': 'Hallo, das ist ein Stimmtest auf Deutsch.',
      'it': 'Ciao, questo è un test vocale in italiano.',
      'pt': 'Olá, este é um teste de voz em português.',
      'ru': 'Привет, это тест голоса на русском языке.',
      'ja': 'こんにちは、これは日本語の音声テストです。',
      'ko': '안녕하세요, 이것은 한국어 음성 테스트입니다.',
      'zh': '你好，这是中文语音测试。',
      'ar': 'مرحبا، هذا اختبار صوتي باللغة العربية.',
      'hi': 'नमस्ते, यह हिंदी में आवाज़ का परीक्षण है।'
    };

    const sampleText = sampleTexts[languageCode] || sampleTexts['en'];
    
    setIsTestingVoice(voice.voiceURI);

    try {
      const utterance = new SpeechSynthesisUtterance(sampleText);
      
      // CRITICAL FIX: Usar la voz original del navegador
      utterance.voice = voice.originalVoice;
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      utterance.onstart = () => {
        console.log(`🎤 Voice test started: ${voice.name}`);
      };

      utterance.onend = () => {
        console.log(`🎤 Voice test ended: ${voice.name}`);
        setIsTestingVoice(null);
        currentUtterance.current = null;
      };

      // CRITICAL FIX: Manejo mejorado de errores SIN mostrar alertas por interrupciones
      utterance.onerror = (event) => {
        console.log(`🎤 Speech synthesis event: ${event.error}`);
        
        // NO mostrar error si fue una interrupción intencional
        if (event.error === 'interrupted' || event.error === 'canceled') {
          console.log(`🎤 Voice test interrupted intentionally: ${voice.name}`);
        } else {
          console.error(`❌ Speech synthesis error: ${event.error}`);
          // Solo mostrar alerta para errores reales, no interrupciones
          alert(`Error en la síntesis de voz: ${event.error}. Intenta con otra voz.`);
        }
        
        setIsTestingVoice(null);
        currentUtterance.current = null;
      };

      currentUtterance.current = utterance;
      speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('❌ Error speaking:', error);
      setIsTestingVoice(null);
      currentUtterance.current = null;
      alert(`Error al iniciar la síntesis de voz: ${error.message}`);
    }
  };

  // Función MEJORADA para detener el test SUAVEMENTE sin errores
  const stopCurrentTestSafely = () => {
    if (currentUtterance.current || speechSynthesis.speaking) {
      console.log(`🛑 Stopping voice test safely`);
      
      // Marcar como detenido ANTES de cancelar para evitar el error
      setIsTestingVoice(null);
      currentUtterance.current = null;
      
      // Cancelar suavemente
      speechSynthesis.cancel();
    }
  };

  const handleVoiceSelect = (voice, event) => {
    event?.stopPropagation();
    console.log(`🎯 Voice selected manually: ${voice.name} (URI: ${voice.voiceURI})`);
    
    // Detener cualquier test en curso SUAVEMENTE
    stopCurrentTestSafely();
    
    // CRITICAL FIX: Pasar la voz con toda su información
    onVoiceChange(voice);
    onClose();
  };

  // Click fuera para cerrar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && 
          dropdownRef.current && 
          !dropdownRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      stopCurrentTestSafely();
      if (voicesCheckInterval.current) {
        clearInterval(voicesCheckInterval.current);
      }
      if (loadingTimeout.current) {
        clearTimeout(loadingTimeout.current);
      }
    };
  }, []);

  const getQualityBadge = (quality) => {
    const badges = {
      premium: { text: 'Premium', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
      enhanced: { text: 'Enhanced', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
      good: { text: 'Good', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      standard: { text: 'Standard', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' }
    };
    
    const badge = badges[quality] || badges.standard;
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const getGenderIcon = (gender) => {
    switch (gender) {
      case 'female':
        return <span className="text-pink-500">♀️</span>;
      case 'male':
        return <span className="text-blue-500">♂️</span>;
      default:
        return <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-500" />;
    }
  };

  // Calcular posición del dropdown dinámicamente
  const getDropdownPosition = () => {
    if (!buttonRef.current) return { top: 0, left: 0 };
    
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    const spaceBelow = viewportHeight - rect.bottom;
    const dropdownHeight = 400;
    
    let top, left;
    
    if (spaceBelow >= dropdownHeight + 20) {
      top = rect.bottom + 8;
    } else {
      top = rect.top - dropdownHeight - 8;
      if (top < 20) {
        top = Math.max(20, (viewportHeight - dropdownHeight) / 2);
      }
    }
    
    left = rect.left;
    
    const dropdownWidth = 480;
    if (left + dropdownWidth > viewportWidth - 20) {
      left = viewportWidth - dropdownWidth - 20;
    }
    if (left < 20) {
      left = 20;
    }
    
    return { top, left };
  };

  // Mostrar estado de carga
  if (loadingVoices || (!voicesLoaded && filteredVoices.length === 0)) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 min-w-[140px]">
        <SafeIcon icon={FiLoader} className="w-4 h-4 text-gray-400 flex-shrink-0 animate-spin" />
        <span className="text-sm text-gray-400 truncate flex-1 text-left">
          {t('loading') || 'Cargando voces...'}
        </span>
      </div>
    );
  }

  return (
    <div className="relative voice-selector-container">
      <motion.button
        ref={buttonRef}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onToggle}
        className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-300 dark:border-gray-600 min-w-[140px] max-w-[220px]"
      >
        <SafeIcon icon={FiVolume2} className="w-4 h-4 text-gray-600 dark:text-gray-300 flex-shrink-0" />
        <div className="flex-1 min-w-0 text-left">
          <span className="text-sm text-gray-700 dark:text-gray-300 truncate block">
            {selectedVoice ? selectedVoice.displayName || selectedVoice.name : 
             voicesLoaded && filteredVoices.length === 0 ? 
             (t('noVoicesAvailable') || 'Sin voces') :
             (t('selectVoice') || 'Seleccionar voz')}
          </span>
          {selectedVoice && (
            <div className="flex items-center space-x-1 mt-0.5">
              {getGenderIcon(selectedVoice.gender)}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {selectedVoice.gender === 'female' ? 'Femenina' : selectedVoice.gender === 'male' ? 'Masculina' : 'Neutral'}
              </span>
            </div>
          )}
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <SafeIcon icon={FiChevronDown} className="w-4 h-4 text-gray-500" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay para cerrar al hacer clic fuera */}
            <div 
              className="fixed inset-0 z-[9998] bg-transparent"
              onClick={onClose}
            />
            
            {/* Dropdown con posicionamiento dinámico */}
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="fixed bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-2xl min-w-[380px] max-w-[480px] max-h-[400px] overflow-hidden z-[9999]"
              style={getDropdownPosition()}
            >
              {/* Header fijo */}
              <div className="sticky top-0 p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 rounded-t-lg z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                      {label} - {t('selectVoice') || 'Seleccionar voz'}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {filteredVoices.length > 0 
                        ? `${filteredVoices.length} ${t('voicesAvailable') || 'voces disponibles'}`
                        : t('noVoicesAvailable') || 'No hay voces disponibles para este idioma'
                      }
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={reloadVoices}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    title="Recargar voces"
                  >
                    <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>
              
              {/* Lista de voces con scroll */}
              <div 
                className="max-h-[320px] overflow-y-auto overflow-x-hidden divide-y divide-gray-100 dark:divide-gray-700"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#cbd5e0 #f7fafc'
                }}
              >
                {filteredVoices.length > 0 ? (
                  filteredVoices.map((voice, index) => (
                    <motion.div
                      key={`${voice.voiceURI}-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.03 }}
                      className={`flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
                        selectedVoice?.voiceURI === voice.voiceURI ? 'bg-purple-50 dark:bg-purple-900/30' : ''
                      }`}
                      onClick={(e) => handleVoiceSelect(voice, e)}
                    >
                      <div className="flex-1 min-w-0 mr-3">
                        {/* Línea 1: Nombre y checkmark */}
                        <div className="flex items-center space-x-2 mb-2">
                          {getGenderIcon(voice.gender)}
                          <span className="font-medium text-gray-900 dark:text-white text-sm truncate max-w-[200px]">
                            {voice.displayName}
                          </span>
                          {selectedVoice?.voiceURI === voice.voiceURI && (
                            <SafeIcon icon={FiCheck} className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                          )}
                        </div>
                        
                        {/* Línea 2: Badges */}
                        <div className="flex items-center space-x-1 mb-2 flex-wrap gap-1">
                          {getQualityBadge(voice.quality)}
                          {voice.gender !== 'neutral' && (
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              voice.gender === 'female' 
                                ? 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                            }`}>
                              {voice.gender === 'female' ? 'Femenina' : 'Masculina'}
                            </span>
                          )}
                          {voice.isLocal && (
                            <span className="px-2 py-1 text-xs rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300 font-medium">
                              {t('local') || 'Local'}
                            </span>
                          )}
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 font-medium">
                            ✓ Funcional
                          </span>
                        </div>
                        
                        {/* Línea 3: Info técnica */}
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[220px]">
                          {voice.lang} • {voice.fullName}
                        </p>
                      </div>
                      
                      {/* Botón de prueba */}
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => testVoice(voice, e)}
                        className={`ml-2 p-2 rounded-full transition-colors flex-shrink-0 ${
                          isTestingVoice === voice.voiceURI
                            ? 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-400'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                        }`}
                        title={isTestingVoice === voice.voiceURI ? 'Detener vista previa' : 'Vista previa de voz'}
                      >
                        <SafeIcon 
                          icon={isTestingVoice === voice.voiceURI ? FiPause : FiPlay} 
                          className={`w-4 h-4 ${isTestingVoice === voice.voiceURI ? 'animate-pulse' : ''}`} 
                        />
                      </motion.button>
                    </motion.div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <SafeIcon icon={FiVolume2} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 mb-3 font-medium">
                      {t('noVoicesAvailable') || 'No hay voces disponibles para este idioma'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                      Intenta con otro idioma o verifica la configuración del sistema
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={reloadVoices}
                      className="flex items-center space-x-2 mx-auto px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                      <span>Recargar voces</span>
                    </motion.button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VoiceSelector;